/**
 * Import Agent Wizard.
 *
 * Four-step flow:
 *   1. Pick file (this task)
 *   2. Validate (Task 16)
 *   3. Review + collision/secrets UX (Task 17)
 *   4. Confirm + result (Task 18)
 *
 * The wizard is self-contained. All transitions live in local state so
 * there's no router coupling and no shared state outside this component.
 * onImport is the only callback that touches the backend; it mirrors
 * AgentContext.importAgent's signature so the wizard can be tested in
 * isolation by passing in a mock.
 */
import { useState } from 'react';
import { Upload, X } from 'lucide-react';
import type { Agent } from '@/types/agent.types';
import type { ExportEnvelope } from '@/services/agentExportImport/schema';
import {
  validateForUser,
  type ValidationResult,
} from '@/services/agentExportImport/validateForUser';
import { findRedactedPaths } from '@/services/agentExportImport/redactSecrets';
import { Button } from '@/components/Common';

type Step = 'pick' | 'validate' | 'review' | 'result';

interface ImportAgentWizardProps {
  isOpen: boolean;
  onClose: () => void;
  existingAgents: Agent[];
  onImport: (args: {
    payload: ExportEnvelope;
    mode: 'create' | 'update';
    targetAgentId?: string;
    secretOverrides?: Record<string, string>;
  }) => Promise<Agent>;
}

/**
 * Walks an agent object collecting every string value found under a key
 * named `endpoint`. The review step renders these verbatim so the admin
 * can sanity-check that a dev-environment export isn't about to be
 * imported into prod with the wrong base URL.
 */
function collectEndpoints(value: unknown, out: string[] = []): string[] {
  if (Array.isArray(value)) {
    value.forEach((v) => collectEndpoints(v, out));
  } else if (value !== null && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === 'endpoint' && typeof v === 'string' && v.length > 0) {
        out.push(v);
      } else {
        collectEndpoints(v, out);
      }
    }
  }
  return out;
}

export function ImportAgentWizard({
  isOpen,
  onClose,
  existingAgents,
  onImport,
}: ImportAgentWizardProps) {
  const [step, setStep] = useState<Step>('pick');
  // rawText is retained for diagnostics; the import payload uses the parsed
  // envelope below.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rawText, setRawText] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [mode, setMode] = useState<'create' | 'update'>('create');
  const [confirmName, setConfirmName] = useState('');
  const [secretInputs, setSecretInputs] = useState<Record<string, string>>({});
  const [importedAgent, setImportedAgent] = useState<Agent | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  if (!isOpen) return null;

  const envelope = validation && validation.ok ? validation.envelope! : null;
  const incoming = envelope?.agent ?? null;
  const collision = incoming
    ? existingAgents.find((a) => a.name === incoming.name) ?? null
    : null;
  const redactedPaths = incoming ? findRedactedPaths(incoming) : [];
  const endpoints = incoming
    ? Array.from(new Set(collectEndpoints(incoming)))
    : [];
  const isImportEnabled =
    mode === 'create' ||
    (mode === 'update' &&
      collision !== null &&
      confirmName === collision.name);

  const handleFile = async (file: File) => {
    setPickError(null);
    try {
      const text = await file.text();
      setRawText(text);
      const result = validateForUser(text);
      setValidation(result);
      setStep(result.ok ? 'review' : 'validate');
    } catch (err) {
      setPickError(
        err instanceof Error
          ? `${err.message}. Try a different file.`
          : 'Could not read that file. Try a different one.'
      );
    }
  };

  const handleConfirmImport = async () => {
    if (!envelope || !incoming) return;

    // Drop empty secret inputs — the import endpoint treats missing
    // overrides as "leave the sentinel, get cleared to empty string".
    const overrides: Record<string, string> = {};
    for (const [path, value] of Object.entries(secretInputs)) {
      if (value.length > 0) overrides[path] = value;
    }

    setIsImporting(true);
    setImportError(null);
    try {
      const result = await onImport({
        payload: envelope,
        mode,
        targetAgentId:
          mode === 'update' ? (collision?.id ?? undefined) : undefined,
        secretOverrides: overrides,
      });
      setImportedAgent(result);
      setStep('result');
    } catch (err) {
      setImportError(
        err instanceof Error
          ? err.message
          : 'Import failed. Check the values you entered and try again.'
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-wizard-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="w-full max-w-2xl rounded-xl bg-bg-primary p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2
            id="import-wizard-title"
            className="text-lg font-semibold text-text-primary"
          >
            Import agent
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-text-tertiary hover:text-text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === 'pick' && (
          <div className="mt-6">
            <p className="text-sm text-text-secondary">
              Choose a Stellar agent export file (.json). Nothing is changed
              until you confirm on the review screen.
            </p>
            <label
              htmlFor="import-wizard-file"
              className="mt-4 flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border-default p-8 hover:bg-bg-secondary"
            >
              <Upload className="h-8 w-8 text-text-tertiary" aria-hidden="true" />
              <span className="text-sm text-text-primary">
                Drag a .json file here, or
              </span>
              <span className="rounded-md bg-stellar-primary px-3 py-1.5 text-sm text-white">
                Choose file
              </span>
              <input
                id="import-wizard-file"
                type="file"
                accept=".json,application/json"
                aria-label="Choose file"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
            </label>
            {pickError && (
              <div
                role="alert"
                className="mt-3 rounded-lg border border-error bg-error-light p-3 text-sm text-error"
              >
                {pickError}
              </div>
            )}
          </div>
        )}

        {step === 'validate' && validation && !validation.ok && (
          <div className="mt-6">
            <p className="text-sm text-text-secondary">
              We couldn&apos;t read that file. Here&apos;s what&apos;s wrong:
            </p>
            <ul className="mt-3 space-y-2">
              {validation.problems.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-error bg-error-light p-3 text-sm"
                >
                  <span aria-hidden="true" className="text-error">✗</span>
                  <span>
                    <span className="block font-medium text-error">{p.message}</span>
                    <span className="block text-text-secondary">{p.hint}</span>
                  </span>
                </li>
              ))}
            </ul>
            <Button
              className="mt-4"
              variant="secondary"
              onClick={() => {
                setValidation(null);
                setRawText(null);
                setStep('pick');
              }}
            >
              Choose a different file
            </Button>
          </div>
        )}

        {step === 'review' && envelope && incoming && (
          <div className="mt-6 space-y-4">
            <h3 className="text-base font-semibold text-text-primary">
              Review what will be imported
            </h3>

            <div className="rounded-lg border border-border-default p-3">
              <div className="text-sm text-text-secondary">Agent</div>
              <div className="font-medium text-text-primary">
                {incoming.name}
              </div>
              <div className="text-xs text-text-tertiary">
                {incoming.framework}
              </div>
            </div>

            {endpoints.length > 0 && (
              <div className="rounded-lg border border-border-default p-3">
                <div className="text-sm text-text-secondary">
                  Endpoints this agent will call
                </div>
                <ul className="mt-1 space-y-1 text-sm font-mono">
                  {endpoints.map((e) => (
                    <li key={e} className="text-text-primary">
                      {e}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-text-tertiary">
                  If any of these still point at the source environment, edit
                  the agent after import.
                </p>
              </div>
            )}

            {redactedPaths.length > 0 && (
              <div className="rounded-lg border border-warning bg-warning-light p-3">
                <div className="text-sm font-medium text-text-primary">
                  Secrets needed
                </div>
                <p className="text-xs text-text-secondary">
                  Paste each key below. You can leave any field blank — the
                  agent will be imported deactivated and you can add the key
                  later in the agent editor.
                </p>
                <div className="mt-2 space-y-2">
                  {redactedPaths.map((path) => (
                    <label key={path} className="block">
                      <span className="block text-xs font-mono text-text-secondary">
                        {path}
                      </span>
                      <input
                        type="password"
                        aria-label={path}
                        value={secretInputs[path] ?? ''}
                        onChange={(e) =>
                          setSecretInputs((s) => ({
                            ...s,
                            [path]: e.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-md border border-border-default px-2 py-1 text-sm"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}

            {collision && (
              <div className="rounded-lg border border-warning bg-warning-light p-3">
                <div className="text-sm font-medium text-text-primary">
                  An agent with this name already exists.
                </div>
                <div className="mt-2 space-y-2 text-sm">
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="import-mode"
                      checked={mode === 'create'}
                      onChange={() => setMode('create')}
                      aria-label="Create a new copy"
                    />
                    <span>
                      Create a new copy
                      <span className="block text-xs text-text-secondary">
                        Imported as &quot;{incoming.name} (imported)&quot;. The
                        existing agent is not touched.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="import-mode"
                      checked={mode === 'update'}
                      onChange={() => setMode('update')}
                      aria-label="Update the existing agent"
                    />
                    <span>
                      Update the existing agent
                      <span className="block text-xs text-text-secondary">
                        Overwrites the configuration on &quot;{collision.name}
                        &quot;. This cannot be undone.
                      </span>
                    </span>
                  </label>
                  {mode === 'update' && (
                    <label className="block">
                      <span className="block text-xs text-text-secondary">
                        Type the existing agent&apos;s name to confirm:{' '}
                        <strong>{collision.name}</strong>
                      </span>
                      <input
                        aria-label="Type the existing agent's name"
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        className="mt-1 w-full rounded-md border border-border-default px-2 py-1 text-sm"
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            {importError && (
              <div
                role="alert"
                className="rounded-lg border border-error bg-error-light p-3 text-sm text-error"
              >
                <div className="font-medium">{importError}</div>
                <Button
                  className="mt-2"
                  variant="secondary"
                  onClick={() => setImportError(null)}
                >
                  Back to review
                </Button>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  // Back to pick — reset wizard state for steps 2/3 so the
                  // old success state can't leak into a fresh file pick.
                  setValidation(null);
                  setSecretInputs({});
                  setConfirmName('');
                  setMode('create');
                  setImportError(null);
                  setStep('pick');
                }}
              >
                Back
              </Button>
              <Button
                disabled={!isImportEnabled || isImporting}
                onClick={handleConfirmImport}
              >
                {isImporting ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && importedAgent && (
          <div className="mt-6 space-y-3">
            <div className="rounded-lg border border-success bg-success-light p-3 text-sm text-text-primary">
              <strong>{importedAgent.name}</strong> imported successfully
              (inactive).
            </div>
            <p className="text-xs text-text-secondary">
              Activate the agent from the Agent Management list when
              you&apos;re ready.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  // Reset the wizard for a fresh import.
                  setStep('pick');
                  setImportedAgent(null);
                  setRawText(null);
                  setValidation(null);
                  setSecretInputs({});
                  setConfirmName('');
                  setMode('create');
                  setImportError(null);
                }}
              >
                Import another
              </Button>
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        )}

        {step !== 'review' && step !== 'result' && (
          <div className="mt-6 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
